import React, {
	createContext,
	useState,
	useContext,
	useEffect,
	useCallback,
} from "react";
import { DEFAULT_CARD_SETTINGS } from "../components/card-components/animalscard/AnimalsCard";

// Context initial state with correct types
const initialContextValue = {
	cardVisibility: DEFAULT_CARD_SETTINGS,
	toggleCardVisibility: () => {},
	updateCardSettings: () => {},
	resetToDefaults: () => {},
	isCardVisible: () => false,
};

// Context for managing dashboard card visibility settings
const CardSettingsContext = createContext(initialContextValue);

// Logging disabled to avoid console output in production
const log = () => {};

/**
 * Load settings from localStorage
 * @returns {Object} Card visibility settings
 */
const loadStoredSettings = () => {
	try {
		const savedSettings = localStorage.getItem("dashboardCardSettings");
		if (savedSettings) {
			log("Loaded settings from localStorage:", savedSettings);
			const parsedSettings = JSON.parse(savedSettings);
			// Ensure all values are boolean
			const cleanedSettings = {};
			Object.entries(parsedSettings).forEach(([key, value]) => {
				cleanedSettings[key] = Boolean(value);
			});

			// Merge with default settings to handle any new cards added since last save
			const mergedSettings = { ...DEFAULT_CARD_SETTINGS, ...cleanedSettings };
			log("Merged with defaults:", mergedSettings);
			return mergedSettings;
		}
	} catch (error) {
		console.error("Failed to parse card settings from localStorage:", error);
	}

	log("Using default settings:", DEFAULT_CARD_SETTINGS);
	return DEFAULT_CARD_SETTINGS;
};

/**
 * Provider component for card visibility settings
 * Manages which dashboard cards are visible to the user
 */
export const CardSettingsProvider = ({ children }) => {
	// Initialize state from localStorage or defaults
	const [cardVisibility, setCardVisibility] = useState(loadStoredSettings);

	// Save settings to localStorage whenever they change
	useEffect(() => {
		try {
			log(
				"Card visibility state changed - saving to localStorage:",
				cardVisibility
			);
			localStorage.setItem(
				"dashboardCardSettings",
				JSON.stringify(cardVisibility)
			);
		} catch (error) {
			console.error("Failed to save card settings to localStorage:", error);
		}
	}, [cardVisibility]);

	/**
	 * Toggle visibility for a specific card
	 * @param {string} cardId - ID of the card to toggle
	 */
	const toggleCardVisibility = useCallback(
		(cardId) => {
			log(
				`Toggling visibility for card ${cardId}, current state:`,
				cardVisibility[cardId]
			);
			setCardVisibility((prev) => {
				const newState = {
					...prev,
					[cardId]: !prev[cardId],
				};
				log(`New state after toggle:`, newState);
				return newState;
			});
		},
		[cardVisibility]
	);

	/**
	 * Update multiple card settings at once
	 * @param {Object} newSettings - Object with cardId keys and boolean values
	 */
	const updateCardSettings = useCallback((newSettings) => {
		log("Updating card settings with:", newSettings);
		setCardVisibility((prev) => {
			const updatedSettings = {
				...prev,
				...newSettings,
			};
			log("Updated settings:", updatedSettings);
			return updatedSettings;
		});
	}, []);

	/**
	 * Reset all card settings to defaults
	 */
	const resetToDefaults = useCallback(() => {
		log("Resetting card settings to defaults:", DEFAULT_CARD_SETTINGS);
		setCardVisibility(DEFAULT_CARD_SETTINGS);
	}, []);

	/**
	 * Check if a card is visible
	 * @param {string} cardId - ID of the card to check
	 * @returns {boolean} - Whether the card is visible
	 */
	const isCardVisible = useCallback(
		(cardId) => {
			const isVisible = Boolean(cardVisibility[cardId]);
			log(`Checking visibility for card ${cardId}:`, isVisible);
			return isVisible;
		},
		[cardVisibility]
	);

	const contextValue = {
		cardVisibility,
		toggleCardVisibility,
		updateCardSettings,
		resetToDefaults,
		isCardVisible,
	};

	return (
		<CardSettingsContext.Provider value={contextValue}>
			{children}
		</CardSettingsContext.Provider>
	);
};

/**
 * Custom hook to access card settings context
 * @returns {Object} Card settings context
 */
export const useCardSettings = () => {
	const context = useContext(CardSettingsContext);
	if (!context) {
		throw new Error(
			"useCardSettings must be used within a CardSettingsProvider"
		);
	}
	return context;
};

export default CardSettingsContext;
